import { useCallback, useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { Spinner } from './Spinner';

interface AuthStatus {
  enabled: boolean;
  authenticated: boolean;
}

type GateState =
  | { phase: 'loading' }
  | { phase: 'error'; message: string }
  | { phase: 'ready'; status: AuthStatus };

const MAX_ATTEMPTS = 3;
const RETRY_DELAYS_MS = [1000, 2000];

function formatRetryAfter(seconds: number): string {
  const minutes = Math.max(1, Math.ceil(seconds / 60));
  if (minutes >= 60) {
    const hours = Math.round(minutes / 60);
    return `${hours} hour${hours === 1 ? '' : 's'}`;
  }
  return `${minutes} minute${minutes === 1 ? '' : 's'}`;
}

function isAuthStatus(data: unknown): data is AuthStatus {
  return (
    typeof data === 'object' &&
    data !== null &&
    typeof (data as AuthStatus).enabled === 'boolean' &&
    typeof (data as AuthStatus).authenticated === 'boolean'
  );
}

export function AuthGate({ children }: { children: ReactNode }) {
  const [gate, setGate] = useState<GateState>({ phase: 'loading' });
  const [secret, setSecret] = useState('');
  const [error, setError] = useState<string | null>(null);

  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      abortRef.current?.abort();
    };
  }, []);

  const loadStatus = useCallback(async () => {
    abortRef.current?.abort();
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    abortRef.current = new AbortController();
    setGate({ phase: 'loading' });

    let lastMessage = "Couldn't reach the server";

    for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
      try {
        const res = await fetch('/api/auth/status', {
          signal: abortRef.current.signal,
        });
        if (!res.ok) {
          throw new Error(`Server returned ${res.status}`);
        }
        const data: unknown = await res.json();
        if (!isAuthStatus(data)) {
          throw new Error('Unexpected response from server');
        }
        if (mountedRef.current) {
          setGate({ phase: 'ready', status: data });
          if (data.enabled && !data.authenticated) {
            setError(null);
          }
        }
        return;
      } catch (e: any) {
        if (e.name === 'AbortError') {
          return;
        }
        lastMessage = e?.message || "Couldn't reach the server";
        if (attempt < MAX_ATTEMPTS) {
          await new Promise<void>((resolve) => {
            timeoutRef.current = setTimeout(() => {
              timeoutRef.current = null;
              resolve();
            }, RETRY_DELAYS_MS[attempt - 1] ?? RETRY_DELAYS_MS[RETRY_DELAYS_MS.length - 1]);
          });
        }
      }
    }

    if (mountedRef.current) {
      setGate({ phase: 'error', message: lastMessage });
    }
  }, []);

  useEffect(() => {
    void loadStatus();
  }, [loadStatus]);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    try {
      const res = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ secret }),
      });
      if (!res.ok) {
        if (res.status === 429) {
          const data = await res.json().catch(() => ({ retryAfterSeconds: 0 }));
          setError(
            `Too many failed attempts. Try again in ${formatRetryAfter(
              Number(data.retryAfterSeconds) || 0,
            )}.`,
          );
        } else {
          setError('Invalid passphrase');
        }
        return;
      }
      await loadStatus();
    } catch (e: any) {
      setError(e.message || 'Login failed');
    }
  }

  if (gate.phase === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Spinner size={32} />
      </div>
    );
  }

  if (gate.phase === 'error') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <div className="w-full max-w-sm bg-white rounded-xl border border-gray-200 p-6 shadow-sm text-center">
          <h1 className="text-xl font-semibold text-gray-900 mb-2">Mission Control</h1>
          <p className="text-sm text-gray-700 mb-1">Couldn't reach the server</p>
          <p className="text-xs text-gray-500 mb-4 break-words">{gate.message}</p>
          <button
            type="button"
            onClick={() => void loadStatus()}
            className="w-full rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (gate.status.enabled && !gate.status.authenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <div className="w-full max-w-sm bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
          <h1 className="text-xl font-semibold text-gray-900 mb-4">Mission Control</h1>
          <p className="text-sm text-gray-600 mb-4">Enter the shared passphrase to continue.</p>
          <form onSubmit={handleSubmit} className="space-y-3">
            <input
              type="password"
              value={secret}
              onChange={(e) => setSecret(e.target.value)}
              placeholder="Passphrase"
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              autoFocus
            />
            {error && <p className="text-sm text-red-600">{error}</p>}
            <button
              type="submit"
              className="w-full rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              Unlock
            </button>
          </form>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
